"use client";

import Image from "next/image";
import { forwardRef, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { SectionBlock } from "@/components/blocks/SectionBlock";
import EmmaPhoto from "@/assets/Emma_about.jpg";
import { AccentStar } from "@/shared/ui/accent-star";
import { useScrollReveal, type RevealState } from "@/shared/lib/useScrollReveal";
import type { Principle } from "@/entities/principle";

/* ─────────────────────────────────────────────────────────────
   Стиль-хелперы
───────────────────────────────────────────────────────────── */

/** Заголовок секции: одним блоком сверху вниз (issue 1)
    DOM: подзаголовок → заголовок */
function headingBlock(state: RevealState): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(-2.5rem)" };
  return {
    opacity: 0,
    animation: "slide-from-top 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards",
  };
}

function fromLeft(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateX(-3rem)" };
  return {
    opacity: 0,
    animation: `slide-from-left 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

function fromBottom(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(2.5rem)" };
  return {
    opacity: 0,
    animation: `slide-from-bottom 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

function starPing(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "scale(0)" };
  return {
    opacity: 0,
    animation: `star-ping-zoom 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay.toFixed(2)}s forwards`,
  };
}

/* ─────────────────────────────────────────────────────────────
   RAF-typewriter для цитаты
───────────────────────────────────────────────────────────── */
const QUOTE_CHARS_PER_SEC = 48;
const QUOTE_START_DELAY_MS = 700;

function useTypewriter(text: string, state: RevealState): string {
  const [displayed, setDisplayed] = useState("");

  useEffect(() => {
    if (state === "done") {
      setDisplayed(text);
      return;
    }
    if (state !== "animating") {
      setDisplayed("");
      return;
    }

    let frameId: number;
    let startTime: number | null = null;

    function tick(now: number) {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime - QUOTE_START_DELAY_MS;

      if (elapsed < 0) {
        frameId = requestAnimationFrame(tick);
        return;
      }

      const chars = Math.min(
        Math.floor((elapsed / 1000) * QUOTE_CHARS_PER_SEC),
        text.length,
      );
      setDisplayed(text.slice(0, chars));

      if (chars < text.length) {
        frameId = requestAnimationFrame(tick);
      }
    }

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [text, state]);

  return displayed;
}

/* ─────────────────────────────────────────────────────────────
   Компонент
───────────────────────────────────────────────────────────── */
interface EmmaAgasaryanSectionProps {
  quote: string;
  principles: Principle[];
}

export const EmmaAgasaryanSection = forwardRef<HTMLElement, EmmaAgasaryanSectionProps>(({ quote, principles }, ref) => {
  const { ref: revealRef, state } = useScrollReveal("emma", 0.1);

  const displayedQuote = useTypewriter(quote, state);

  const setRefs = (el: HTMLElement | null) => {
    revealRef(el);
    if (typeof ref === "function") ref(el);
    else if (ref)
      (ref as React.MutableRefObject<HTMLElement | null>).current = el;
  };

  return (
    <section ref={setRefs} className="flex flex-col">
      <SectionBlock className="flex flex-col gap-fluid-2xl p-fluid-xl">

        {/* Заголовок: одним блоком сверху вниз */}
        <div className="overflow-hidden">
          <div style={headingBlock(state)} className="text-left">
            <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground">
              Руководитель центра{" "}
              <span className="text-help">Эмма Агасарян</span>
            </h2>
            <p className="font-content text-fluid-base leading-relaxed text-foreground/80 mt-fluid-sm max-w-[65ch]">
              Врач-психиатр, психотерапевт, к.м.н., президент АКБТ,
              аккредитованный EABCT и ISST тренер и супервизор
            </p>
          </div>
        </div>

        {/* Фото + Цитата */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-fluid-2xl pt-fluid-lg border-t border-[var(--help)]/20">

          {/* Фото: влёт слева */}
          <div
            style={fromLeft(state, 0.3)}
            className="w-full lg:w-2/5 shrink-0"
          >
            <div className="relative aspect-[4/5] rounded-[var(--fluid-radius-lg)] overflow-hidden [container-type:size]">
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[150%] min-h-[60cqh] h-[100%] md:left-0 md:translate-x-0 md:w-[100%] md:h-[110%] md:min-h-0 z-10 pointer-events-none">
                <Image
                  src={EmmaPhoto}
                  alt="Эмма Агасарян"
                  fill
                  className="object-contain object-bottom"
                  sizes="(max-width: 1024px) 90vw, 40vw"
                  priority
                />
              </div>
            </div>
          </div>

          {/* Цитата: typewriter + grid-stacking против прыжков высоты */}
          <div className="w-full lg:w-3/5">
            <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>

              {/* Сайзер: полный текст всегда занимает место, но невидим */}
              <blockquote
                aria-hidden
                style={{ gridColumn: 1, gridRow: 1, visibility: "hidden" }}
                className="text-fluid-lg text-foreground font-content italic leading-relaxed border-l-[3px] border-transparent pl-fluid-md"
              >
                <span className="not-italic font-heading text-help/40 text-fluid-2xl leading-none mr-1 align-top select-none">
                  «
                </span>
                {quote}
              </blockquote>

              {/* Анимированная цитата поверх сайзера */}
              <blockquote
                style={{ gridColumn: 1, gridRow: 1 }}
                className="text-fluid-lg text-foreground font-content italic leading-relaxed border-l-[3px] border-help/40 pl-fluid-md"
              >
                <span className="not-italic font-heading text-help/40 text-fluid-2xl leading-none mr-1 align-top select-none" aria-hidden="true">
                  «
                </span>
                {displayedQuote}
                {state === "animating" && displayedQuote.length < quote.length && (
                  <span
                    className="inline-block w-[2px] h-[1em] bg-current align-middle ml-[2px]"
                    style={{ animation: "cursor-blink 0.7s step-end infinite" }}
                  />
                )}
              </blockquote>

            </div>
          </div>
        </div>

        {/* Детали */}
        <div className="flex flex-col gap-fluid-2xl pt-fluid-lg border-t border-[var(--help)]/20">
          <div className="flex flex-col gap-fluid-lg">

            <p
              style={fromBottom(state, 0.4)}
              className="font-content text-fluid-lg text-foreground/80 leading-relaxed"
            >
              Мы создаем такие условия, чтобы сильные и самодостаточные специалисты оставались с нами надолго.
            </p>

            <div className="flex flex-col gap-fluid-sm">
              <h4
                style={fromBottom(state, 0.5)}
                className="text-fluid-xl font-heading text-foreground"
              >
                Ключевые принципы в работе с командой
              </h4>

              <ul className="flex flex-col gap-fluid-sm">
                {principles.map((p, i) => {
                  const delay = 0.6 + i * 0.15;
                  return (
                    <li
                      key={p.id}
                      style={fromBottom(state, delay)}
                      className="flex items-start gap-fluid-sm text-fluid-base text-foreground/80 font-content"
                    >
                      <div className="mt-1.5 shrink-0">
                        <AccentStar
                          color="var(--help)"
                          size={16}
                          style={starPing(state, delay)}
                        />
                      </div>
                      <span className="leading-snug"><strong>{p.bold}</strong> {p.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="pt-fluid-lg border-t border-border">
            <a
              href="mailto:hello@practicum.group"
              style={fromBottom(state, 1.05)}
              className="btn-solid w-full sm:w-auto whitespace-nowrap"
            >
              Написать руководителю
            </a>
          </div>
        </div>

      </SectionBlock>
    </section>
  );
});

EmmaAgasaryanSection.displayName = "EmmaAgasaryanSection";
